import apiClient, { clearLegacyAuthStorage } from './api';
import type {
  SignupRequest,
  SignupResponse,
  VerifyEmailRequest,
  VerifyEmailResponse,
  ResendVerificationRequest,
  ResendVerificationResponse,
  InvitePublic,
  AcceptInviteRequest,
  AcceptInviteResponse,
  LoginRequest,
  LoginResponse,
  RefreshTokenResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  CheckVerificationResponse,
  User,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ChangePasswordResponse,
  GuestSessionResponse,
  GuestBudgetResponse,
  RoleComputePresetDTO,
  MePatchResponse,
} from '../types/auth';

class AuthService {
  async signup(data: SignupRequest): Promise<SignupResponse> {
    const response = await apiClient.post<SignupResponse>('/auth/signup', data);
    return response.data;
  }

  async verifyEmail(data: VerifyEmailRequest): Promise<VerifyEmailResponse> {
    const response = await apiClient.post<VerifyEmailResponse>('/auth/verify-email', data);
    return response.data;
  }

  async resendVerification(data: ResendVerificationRequest): Promise<ResendVerificationResponse> {
    const response = await apiClient.post<ResendVerificationResponse>('/auth/resend-verification', data);
    return response.data;
  }

  async getInvite(token: string): Promise<InvitePublic> {
    const response = await apiClient.get<InvitePublic>(`/auth/invite/${encodeURIComponent(token)}`);
    return response.data;
  }

  async acceptInvite(data: AcceptInviteRequest): Promise<AcceptInviteResponse> {
    const response = await apiClient.post<AcceptInviteResponse>('/auth/accept-invite', data);
    return response.data;
  }

  async guestSession(): Promise<GuestSessionResponse> {
    const response = await apiClient.post<GuestSessionResponse>('/auth/guest', {});
    clearLegacyAuthStorage();
    return response.data;
  }

  async getGuestBudget(): Promise<GuestBudgetResponse> {
    const response = await apiClient.get<GuestBudgetResponse>('/auth/guest-budget');
    return response.data;
  }

  async getRoleComputePresets(): Promise<RoleComputePresetDTO[]> {
    const response = await apiClient.get<RoleComputePresetDTO[]>('/auth/role-compute-presets');
    return response.data;
  }

  async login(email: string, password: string): Promise<LoginResponse> {
    // Drop stale httpOnly cookies so a failed /auth/me does not refresh with an old invalid token.
    try {
      await apiClient.post('/auth/logout', {}, { _skipAuthRefresh: true });
    } catch {
      // No session yet — ignore.
    }

    const params = new URLSearchParams();
    params.append('username', email);
    params.append('password', password);

    const response = await apiClient.post<LoginResponse>('/auth/login', params, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    clearLegacyAuthStorage();
    return response.data;
  }

  async refreshToken(): Promise<RefreshTokenResponse> {
    const response = await apiClient.post<RefreshTokenResponse>('/auth/refresh', {});
    clearLegacyAuthStorage();
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout', {}, { _skipAuthRefresh: true });
    } finally {
      clearLegacyAuthStorage();
    }
  }

  async forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    const response = await apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', data);
    return response.data;
  }

  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await apiClient.post<ResetPasswordResponse>('/auth/reset-password', data);
    return response.data;
  }

  async checkVerification(email: string): Promise<CheckVerificationResponse> {
    const response = await apiClient.get<CheckVerificationResponse>(
      `/auth/check-verification/${encodeURIComponent(email)}`
    );
    return response.data;
  }

  async getCurrentUser(options?: { skipAuthRefresh?: boolean }): Promise<User> {
    const response = await apiClient.get<User>('/auth/me', {
      _skipAuthRefresh: options?.skipAuthRefresh,
    });
    return response.data;
  }

  async updateProfile(data: UpdateProfileRequest): Promise<MePatchResponse> {
    const response = await apiClient.patch<MePatchResponse>('/auth/me', data);
    clearLegacyAuthStorage();
    return response.data;
  }

  async changePassword(data: ChangePasswordRequest): Promise<ChangePasswordResponse> {
    const response = await apiClient.patch<ChangePasswordResponse>('/auth/me/password', data);
    return response.data;
  }

  async deleteAccount(): Promise<void> {
    await apiClient.delete('/auth/me');
    clearLegacyAuthStorage();
  }

  async testToken(): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/login/test-token');
    return response.data;
  }
}

export default new AuthService();
